import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["getIsUngated", "onChange", "onBlur", "error", "field", "hideExtraInputs", "getTimezoneOptions", "alignToEndOfDay", "value", "condition", "useOnePanel"];
import I18n from 'I18n';
import { syntheticEvent } from 'foundations-components/transitional/utils/SyntheticEvent';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
import { getTransferableProps } from './filterInputProps';
import { fromSimpleDateToMoment, fromMomentToSimpleDate } from 'customer-data-filters-ui/utils/momentSimpleDateConversion';
import TimezoneToggle from './TimezoneToggle';
import UIButton from 'UIComponents/button/UIButton';
import UITimeInput from 'UIComponents/input/UITimeInput';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ONE_MINUTE_BEFORE_MIDNIGHT, getCurrentTimeValue, getNewTimeValue, getTimeValueFromMoment } from '../../utils/timeValueUtils';
import getConditionId from '../../utils/getConditionId';
import { FlexDateInputWithTimezoneWrapper } from '../../constants/inlineLayout';
import TimezoneDropdown from './TimezoneDropdown';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getStateFromValue = (value, useEndOfDay) => {
  let timeValue;
  if (value) {
    timeValue = getTimeValueFromMoment(value);
    const isUsingTimeValue = useEndOfDay ? timeValue < ONE_MINUTE_BEFORE_MIDNIGHT : timeValue > 0;
    return {
      isUsingTimeValue,
      isUTC: value.isUTC(),
      timeValue
    };
  }
  return {
    isUsingTimeValue: false,
    isUTC: false,
    timeValue: useEndOfDay ? ONE_MINUTE_BEFORE_MIDNIGHT : 0
  };
};
const FilterOperatorDateInputMomentWithTime = props => {
  const {
      getIsUngated,
      onChange,
      onBlur,
      error,
      field,
      hideExtraInputs,
      getTimezoneOptions,
      alignToEndOfDay,
      value,
      condition,
      useOnePanel
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const {
    isUsingTimeValue,
    isUTC,
    timeValue
  } = getStateFromValue(value, alignToEndOfDay);
  const handleChange = evt => {
    // @ts-expect-error we added types to `fromSimpleDateToMoment` after this was written
    const newDate = fromSimpleDateToMoment(evt.target.value, isUTC);
    const newValue = getNewTimeValue(newDate, timeValue);
    onChange(syntheticEvent(newValue));
  };
  const handleTimezoneChange = newUseUTC => {
    if (!value) {
      return;
    }
    let newValue;
    if (isUsingTimeValue) {
      //NOTE: as written, if a time has been added, then the date may change if timezone is toggled
      newValue = newUseUTC ? I18n.moment.utc(value) :
      // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
      I18n.moment.portalTz(value);
    } else {
      newValue = newUseUTC ? I18n.moment.utc(value.format('YYYY-MM-DD HH:mm:ss.SSS')) : I18n.moment.portalTz(value.format('YYYY-MM-DD HH:mm:ss.SSS'));
    }
    onChange(syntheticEvent(newValue));
  };
  const handleOpenChange = evt => {
    if (!evt.target.value && onBlur) {
      onBlur();
    }
  };
  const handleAddTime = () => {
    if (value) {
      //TODO: edge case: won't add time if current time in chosen tz is midnight and operator is "before"
      const newTimeValue = getCurrentTimeValue(isUTC);
      onChange(syntheticEvent(getNewTimeValue(value, newTimeValue)));
    }
  };
  const handleRemoveTime = () => {
    if (value) {
      const newTimeValue = alignToEndOfDay ? ONE_MINUTE_BEFORE_MIDNIGHT : 0;
      onChange(syntheticEvent(getNewTimeValue(value, newTimeValue)));
    }
  };
  const handleTimeChange = evt => {
    if (value) {
      const newTimeValue = evt.target.value;
      onChange(syntheticEvent(getNewTimeValue(value, newTimeValue)));
    }
  };
  const isError = error.get('error');
  const errorMessage = error.get('message');
  const transferableProps = getTransferableProps(rest);
  const {
    ['data-selenium-test']: dataSeleniumTest
  } = transferableProps;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const disabled = transferableProps.disabled;
  // @ts-expect-error allows us to avoid spreading props to UIComponents
  const readOnly = transferableProps.readOnly;
  const InputWrapper = useOnePanel ? FlexDateInputWithTimezoneWrapper : 'div';
  const TimezoneSelector = useOnePanel ? TimezoneDropdown : TimezoneToggle;
  return /*#__PURE__*/_jsxs(InputWrapper, {
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": I18n.text('customerDataFiltersUI.customerDataFilters.FilterOperatorInput.ariaLabel'),
      error: isError,
      validationMessage: isError ? errorMessage : null,
      children: /*#__PURE__*/_jsx(UIDateInput, {
        "data-selenium-test": dataSeleniumTest,
        disabled: disabled,
        readOnly: readOnly,
        onChange: handleChange,
        onOpenChange: handleOpenChange,
        value: fromMomentToSimpleDate(value)
      })
    }), isUsingTimeValue ? /*#__PURE__*/_jsxs(View, {
      marginBlock: "200",
      as: "div",
      children: [/*#__PURE__*/_jsx(UITimeInput, {
        "data-selenium-test": dataSeleniumTest,
        disabled: disabled,
        readOnly: readOnly,
        onChange: handleTimeChange,
        value: timeValue
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: handleRemoveTime,
        use: "transparent",
        size: "sm",
        responsive: false,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFiltersUI.customerDataFilters.FilterOperatorDateInputMomentWithTime.removeTime"
        })
      })]
    }) : /*#__PURE__*/_jsx(UIButton, {
      onClick: handleAddTime,
      use: "transparent",
      size: "sm",
      responsive: false,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.customerDataFilters.FilterOperatorDateInputMomentWithTime.addTime"
      })
    }), !hideExtraInputs && /*#__PURE__*/_jsx(TimezoneSelector, {
      conditionId: getConditionId(condition),
      timezoneOptions: getTimezoneOptions(field),
      fieldType: field.get('type'),
      useUTC: isUTC,
      onChange: handleTimezoneChange,
      getIsUngated: getIsUngated
    })]
  });
};

// eslint-disable-next-line react/no-multi-comp
export const FilterOperatorEndOfDateInputMomentWithTime = props => {
  return /*#__PURE__*/_jsx(FilterOperatorDateInputMomentWithTime, Object.assign({}, props, {
    alignToEndOfDay: true
  }));
};

// eslint-disable-next-line react/no-multi-comp
export const FilterOperatorStartOfDateInputMomentWithTime = props => {
  return /*#__PURE__*/_jsx(FilterOperatorDateInputMomentWithTime, Object.assign({}, props, {
    alignToEndOfDay: false
  }));
};
export default FilterOperatorDateInputMomentWithTime;